/**
 * "IT was asked to close this ticket", remembered for the browser session.
 *
 * Closing an untriaged incident does not close it: the backend asks IT to and
 * returns the detail unchanged (`closure_requested`). The page would then look
 * exactly as before, Close button and all, and people press it again. So the
 * request is remembered per ticket id in `sessionStorage` — long enough to
 * cover IT's turnaround, short enough not to outlive a shared-PC session.
 */

import { useCallback, useState } from "react";

const STORAGE_PREFIX = "it-helpdesk-agent.closure-requested.";

function read(ticketId: string): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + ticketId) !== null;
  } catch {
    return false;
  }
}

function write(ticketId: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + ticketId, String(Date.now()));
  } catch {
    // Blocked storage: the in-memory state below still covers this page.
  }
}

/** Whether closure was requested for `ticketId` this session, and a setter. */
export function useClosureRequested(ticketId: string): {
  requested: boolean;
  markRequested: () => void;
} {
  const [requestedFor, setRequestedFor] = useState<Record<string, boolean>>({});
  const requested = requestedFor[ticketId] ?? read(ticketId);
  const markRequested = useCallback(() => {
    write(ticketId);
    setRequestedFor((previous) => ({ ...previous, [ticketId]: true }));
  }, [ticketId]);
  return { requested, markRequested };
}
